import { applySheets } from "../../../app/utils/styleCache.js";
import { apiFetch } from "../../../app/apiClient.js";
import { avatarColor, computeInitials } from "../../../app/utils/avatar.js";
import "../../shared/app-loading-dots/app-loading-dots.js";

const sheets = [
    'components/features/app-panel-list/app-panel-list.css',
    'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css',
];

export class AppPanelList extends HTMLElement {
    config = null;
    items = [];
    selectedId = null;
    initialLoadDone = false;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.closeContextMenus = (event) => {
            if (event.composedPath().some(element => element === this)) return;
            this.shadowRoot?.querySelectorAll('.context-menu.open').forEach(menu => menu.classList.remove('open'));
        };
    }

    connectedCallback() {
        this.shadowRoot.innerHTML = `
            <div class="panel">
                <div class="header"><span class="title"></span></div>
                <div class="search-wrap">
                    <input class="search" type="text" placeholder="Suchen…" />
                </div>
                <div class="list"></div>
            </div>
        `;
        applySheets(this.shadowRoot, sheets);
        this.shadowRoot.querySelector('.search').addEventListener('input', event => {
            this.render(event.target.value.toLowerCase());
        });
        document.addEventListener('click', this.closeContextMenus);
    }

    disconnectedCallback() {
        if (this.onDataChanged && this.dataChangedEvent) {
            document.removeEventListener(this.dataChangedEvent, this.onDataChanged);
        }
        document.removeEventListener('click', this.closeContextMenus);
    }

    configure(config) {
        if (this.onDataChanged && this.dataChangedEvent) {
            document.removeEventListener(this.dataChangedEvent, this.onDataChanged);
            this.onDataChanged = null;
        }
        this.config = config;
        this.selectedId = null;
        this.initialLoadDone = false;

        const titleElement = this.shadowRoot.querySelector('.title');
        if (titleElement) titleElement.textContent = config.title ?? '';

        this.load();

        if (config.dataChangedEvent) {
            this.dataChangedEvent = config.dataChangedEvent;
            this.onDataChanged = async (event) => {
                if (!event.detail?.skipReload && event.detail?.action !== 'applications-updated') await this.load();
                this.config.onDataChanged?.(this, event);
            };
            document.addEventListener(config.dataChangedEvent, this.onDataChanged);
        }
    }

    getItem(id) {
        return this.items.find(item => String(item.id) === String(id)) ?? null;
    }

    updateItem(id, updateFn) {
        const idx = this.items.findIndex(item => String(item.id) === String(id));
        if (idx === -1) return;
        this.items[idx] = updateFn(this.items[idx]);
        this.render(this.shadowRoot.querySelector('.search')?.value.toLowerCase() ?? '');
    }

    addItem(item) {
        this.items.push(item);
        this.render(this.shadowRoot.querySelector('.search')?.value.toLowerCase() ?? '');
    }

    flashItem(id, type = 'create') {
        const cls = `flash-${type}`;
        const element = this.shadowRoot.querySelector(`.item[data-id="${id}"]`);
        if (!element) return;
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        element.classList.remove('flash-create', 'flash-update');
        void element.offsetWidth;
        element.classList.add(cls);
        setTimeout(() => element.classList.remove(cls), 1900);
    }

    async load() {
        const list = this.shadowRoot.querySelector('.list');
        if (list) list.innerHTML = `<div class="list-loading"><app-loading-dots></app-loading-dots></div>`;

        const items = await apiFetch(this.config.endpoint);
        if (!Array.isArray(items)) return;
        this.items = items;
        this.render(this.shadowRoot.querySelector('.search')?.value.toLowerCase() ?? '');

        if (!this.initialLoadDone) {
            this.initialLoadDone = true;
            const initId = this.config.initialSelectedId;
            if (initId) {
                const item = items.find(item => String(item.id) === String(initId));
                if (item) this.selectItem(String(item.id));
            }
        }
    }

    render(filter = '') {
        if (!this.config) return;
        const { getName, getSubtitle, getAvatarStr, actions, getIsDisabled } = this.config;
        const allActions = actions ?? [];
        const list = this.shadowRoot.querySelector('.list');
        if (!list) return;

        const sortByName = (a, b) => (getName(a) ?? '').localeCompare(getName(b) ?? '', undefined, { sensitivity: 'base' });

        let items = filter
            ? this.items.filter(item => getName(item)?.toLowerCase().includes(filter)) : this.items;

        let activeItems, disabledItems;
        if (getIsDisabled) {
            activeItems  = items.filter(item => !getIsDisabled(item)).sort(sortByName);
            disabledItems = items.filter(item =>  getIsDisabled(item)).sort(sortByName);
            if (this.selectedId && disabledItems.some(i => String(i.id) === this.selectedId)) {
                this.selectedId = null;
                this.config.onDeselect?.();
            }
        } else {
            activeItems   = [...items].sort(sortByName);
            disabledItems = [];
        }

        if (this.selectedId) {
            const exists = [...activeItems, ...disabledItems].some(i => String(i.id) === this.selectedId);
            if (!exists) {
                this.selectedId = null;
                this.config.onDeselect?.();
            }
        }

        const renderItemHtml = (item, disabled) => {
            const nameStr    = getName(item) ?? '';
            const avatarStr  = (getAvatarStr ? getAvatarStr(item) : null) || nameStr;
            const itemVisible = allActions
                .map((a, idx) => ({ a, idx }))
                .filter(({ a }) => (a.permission?.() ?? true) && (!a.showFor || a.showFor(item)));
            const actionsHtml = itemVisible.length ? `
                <div class="item-actions">
                    <button class="context-btn"><i class="fa-solid fa-ellipsis-vertical"></i></button>
                    <div class="context-menu">
                        ${itemVisible.map(({ a, idx }, i) => `
                            ${a.danger && i > 0 ? '<div class="ctx-separator"></div>' : ''}
                            <button class="ctx-item${a.danger ? ' danger' : ''}" data-action-idx="${idx}" data-item-id="${item.id}">
                                <i class="fa-solid ${a.icon}"></i> ${a.label}
                            </button>
                        `).join('')}
                    </div>
                </div>` : '';
            return `
                <div class="item${disabled ? ' disabled' : ''}${String(item.id) === this.selectedId ? ' selected' : ''}" data-id="${item.id}">
                    <div class="avatar" style="background:${avatarColor(avatarStr)}">${computeInitials(avatarStr)}</div>
                    <div class="info">
                        <span class="name">${nameStr || '—'}</span>
                        <span class="sub">${getSubtitle ? getSubtitle(item) : ''}</span>
                    </div>
                    ${actionsHtml}
                </div>
            `;
        };

        const divider = disabledItems.length && activeItems.length ? '<div class="disabled-divider"></div>' : '';
        list.innerHTML =
            activeItems.map(item => renderItemHtml(item, false)).join('') +
            divider +
            disabledItems.map(item => renderItemHtml(item, true)).join('');

        list.querySelectorAll('.item:not(.disabled)').forEach(element => {
            element.addEventListener('click', () => this.selectItem(element.dataset.id));
        });

        list.querySelectorAll('.context-btn').forEach(btn => {
            btn.addEventListener('click', event => {
                event.stopPropagation();
                const menu = btn.nextElementSibling;
                const wasOpen = menu.classList.contains('open');
                list.querySelectorAll('.context-menu.open').forEach(menu => menu.classList.remove('open'));
                if (!wasOpen) menu.classList.add('open');
            });
        });

        list.querySelectorAll('.ctx-item').forEach(btn => {
            btn.addEventListener('click', event => {
                event.stopPropagation();
                const actionIdx = parseInt(btn.dataset.actionIdx);
                const itemId = btn.dataset.itemId;
                const item = this.items.find(item => String(item.id) === String(itemId));
                if (item) allActions[actionIdx]?.handler?.(item);
                btn.closest('.context-menu')?.classList.remove('open');
            });
        });
    }

    selectItem(id, { silent = false } = {}) {
        if (this.selectedId === id) {
            this.selectedId = null;
            this.render(this.shadowRoot.querySelector('.search')?.value.toLowerCase() ?? '');
            if (!silent) this.config.onDeselect?.();
            return;
        }
        this.selectedId = id;
        this.render(this.shadowRoot.querySelector('.search')?.value.toLowerCase() ?? '');
        if (silent) return;
        const item = this.items.find(item => String(item.id) === String(id));
        this.config.onSelect?.(item);
    }
}

customElements.define('app-panel-list', AppPanelList);